import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { PessoaService } from './pessoa.service';
import { Pessoa } from '../models/pessoa';
import { Observable, catchError, forkJoin, lastValueFrom, of, tap } from 'rxjs';
import { Tenant } from '../models/tenant';
import { Empresa } from '../models/empresa';
import { EmpresaService } from './empresa.service';
import {getCookie} from "../utils/geral";

@Injectable()
export class ProfileService {
  constructor(
    private authService: AuthService,
    private pessoaService: PessoaService,
    private empresaService: EmpresaService
  ) {}

  temAcessoPortalCedente() {
    return this.authService.loadUserProfile().then((profile: any) => {
      try {
        return profile?.attributes?.portal_cedente[0] === 'true';
      } catch (e) {
        return Promise.resolve(false);
      }
    });
  }

  temAcessoPortalSacado() {
    return this.authService.loadUserProfile().then((profile: any) => {
      try {
        return profile?.attributes?.portal_sacado[0] === 'true';
      } catch (e) {
        return Promise.resolve(false);
      }
    });
  }

  temAcessoPortalInvestidor() {
    return this.authService.loadUserProfile().then((profile: any) => {
      try {
        return profile?.attributes?.portal_investidor[0] === 'true';
      } catch (e) {
        return Promise.resolve(false);
      }
    });
  }

  getAcessosEmpresas(): Promise<Map<string, string[]>> {
    return this.authService.loadUserProfile().then((profile: any) => {
      let map: Map<string, string[]> = new Map();
      try {
        if (
          profile?.attributes?.cedentes &&
          profile?.attributes?.cedentes.length > 0
        ) {
          profile?.attributes?.cedentes.forEach((c: string) => {
            this.setaNoMapIds(map, c);
          });
        }

        if (profile?.attributes?.sacados && 
            profile?.attributes?.sacados.length > 0
          ) {
          profile?.attributes?.sacados.forEach((s: string) => {
            this.setaNoMapIds(map, s);
          });
        }

        if (
          profile?.attributes?.investidores &&
          profile?.attributes?.investidores.length > 0
        ) {
          profile?.attributes?.investidores.forEach((c: string) => {
            this.setaNoMapIds(map, c);
          });
        }

        return map;
      } catch (e) {
        return Promise.resolve(map);
      }
    });
  }

  setaNoMapIds(map: Map<string, string[]>, txt: string) {
    let spl = String(txt).split(':');
    let doc = spl.shift();
    let id = spl.join(':');

    if (doc && id) {
      if (map.has(doc)) {
        map.get(doc)?.push(id);
      } else {
        map.set(doc, [id]);
      }
    }
  }

  setaNoMapEmpresa(map: Map<string, Empresa[]>, doc: string, empresa: Empresa) {
    if (doc && empresa) {
      if (map.has(doc)) {
        map.get(doc)?.push(empresa);
      } else {
        map.set(doc, [empresa]);
      }
    }
  }

  getAcessosCedentes() {
    return this.authService.loadUserProfile().then((profile: any) => {
      try {
        return profile?.attributes?.cedentes;
      } catch (e) {
        return Promise.resolve([]);
      }
    });
  }

  getAcessosSacados() {
    return this.authService.loadUserProfile().then((profile: any) => {
      try {
        return profile?.attributes?.sacados;
      } catch (e) {
        return Promise.resolve([]);
      }
    });
  }

  getAcessosInvestidores() {
    return this.authService.loadUserProfile().then((profile: any) => {
      try {
        return profile?.attributes?.investidores;
      } catch (e) {
        return Promise.resolve([]);
      }
    });
  }

  getAcessosInvestidor() {
    return this.authService.loadUserProfile().then((profile: any) => {
      try {
        let ids = profile?.attributes?.investidores.map(
          (e: string) => e.split(':')[0]
        );
        return ids;
      } catch (e) {
        return Promise.resolve([]);
      }
    });
  }

  getEmpresas(): Promise<Map<string, Empresa[]>> {
    const me = this;
    const tenantId = getCookie('tenant_id');
    return new Promise<Map<string, Empresa[]>>((resolve, reject) => {
      const map: Map<string, Empresa[]> = new Map();

      this.getAcessosEmpresas().then((ids) => {
        const sources$: Observable<any>[] = [];

        const filteredIds = tenantId
          ? new Map([...ids].map(([key, value]) => [key, value.filter(v => v.startsWith(tenantId))]))
          : ids;

        filteredIds.forEach(function (ids, doc) {
          const obs$ = me.empresaService.listById(ids).pipe(
            tap((response: any) => {
              response.forEach((empresa: Empresa) =>
                me.setaNoMapEmpresa(map, doc, empresa)
              );
            }),
            catchError(() => of([])),
          );
          sources$.push(obs$);
        });

        if (sources$.length === 0) {
          resolve(map);
          return;
        }

        forkJoin(sources$).subscribe({
          next: () => resolve(map),
          error: () => resolve(map),
        });
      }).catch(() => resolve(map));
    });
  }

  getTenants(map: Map<string, Empresa[]>): Tenant[] {
    let tenants: Tenant[] = [];
    map.forEach((value: Empresa[], key: String) => {
      value.forEach((p: any) => {
        let tenant: Tenant = {
          tenant: p.tenant,
          nome: p.grupo,
        };
        tenants.push(tenant);
      });
    });
    tenants = tenants.filter(
      (thing, i, arr) => arr.findIndex((t) => t.tenant === thing.tenant) === i
    );
    return tenants;
  }

  async getCedentes(): Promise<Pessoa[]> {
    let acessos = await this.getAcessosCedentes();

    if (acessos && acessos.length > 0) {
      const obs$ = this.pessoaService.listByAcessos(acessos);
      return await lastValueFrom(obs$);
    } else {
      return Promise.resolve([]);
    }
  }

  async getSacados(): Promise<Pessoa[]> {
    let acessos = await this.getAcessosSacados();

    if (acessos && acessos.length > 0) {
      const obs$ = this.pessoaService.listByAcessos(acessos);
      return await lastValueFrom(obs$);
    } else {
      return Promise.resolve([]);
    }
  }

  async getInvestidores(): Promise<Pessoa[]> {
    let acessos = await this.getAcessosInvestidores();
    
    if (acessos && acessos.length > 0) {
      const obs$ = this.pessoaService.listByAcessos(acessos);
      return await lastValueFrom(obs$);
    } else {
      return Promise.resolve([]);
    }
  }
}
